<template>
  <div class="modern-form">
    <v-container>
      <div v-if="loading" class="loading-container">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
          class="loading-spinner" />
      </div>
      <v-row v-else class="mt-3">
        <v-col cols="12" sm="12" md="4">
          <div class="appt-sidebar">
            <div class="sidebar-header">
              <h3 class="sidebar-title">Appointment Types</h3>
            </div>
            <div class="sidebar-content">
              <div class="appt-list">
                <div v-if="acrewAppointmentTypes.length === 0" class="no-results">
                  <v-icon class="no-results-icon">mdi-calendar-blank</v-icon>
                  <p class="no-results-text">No appointment types configured</p>
                </div>
                <div v-else class="appts-list">
                  <div
                    v-for="(appointmentType, i) in acrewAppointmentTypes"
                    :key="i"
                    @click="selectAppointmentType(appointmentType); selectedApptIndex = i"
                    class="appt-item"
                    :class="{ 'selected': selectedApptIndex === i }">
                    <v-icon class="appt-icon">mdi-calendar-clock</v-icon>
                    <span class="appt-name">{{ appointmentType.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="12" md="8">
          <div v-if="!form.id && acrewAppointmentTypes.length === 0" class="no-selection">
            <v-icon class="no-selection-icon">mdi-calendar-blank</v-icon>
            <p class="no-selection-text">No appointment types found</p>
            <v-btn class="btn-primary mt-4" @click="showOpenDentalTypes = true">
              <v-icon left small>mdi-import</v-icon>
              Import from Open Dental
            </v-btn>
          </div>
          <div v-else class="info-section">
            <div class="section-header-with-actions">
              <h2 class="section-title">Appointment Configuration</h2>
              <v-btn class="btn-primary" @click="showOpenDentalTypes = true">
                <v-icon left small>mdi-import</v-icon>
                Import from Open Dental
              </v-btn>
            </div>

            <div class="form-row">
              <v-text-field v-model="form.name" label="Appointment Type Name" class="line-input custom-filled" dense hide-details="auto" />
              <v-text-field v-model="form.codes" label="Billing Codes (Comma Separated)" class="line-input custom-filled" dense hide-details="auto" />
            </div>
            <div class="form-row">
              <v-text-field v-model="form.pattern" label="Time Pattern" class="line-input custom-filled" dense hide-details="auto" />
              <v-text-field v-model="form.appointmentLength" label="Appointment Length (Minutes)" class="line-input custom-filled" dense hide-details="auto" />
            </div>
            <div class="form-row">
              <v-text-field v-model="form.rank" label="Display Order" class="line-input custom-filled" dense hide-details="auto" />
              <v-autocomplete
                class="line-input custom-filled"
                v-model="form.operatories"
                :items="allOperatories"
                item-text="OpName"
                item-value="OperatoryNum"
                label="Operatories Available"
                multiple
                dense
                hide-details="auto" />
            </div>

            <div class="checkbox-group">
              <v-checkbox v-model="form.isMinor" label="Is for minors (<18)?" hide-details />
              <v-checkbox v-model="form.isAdult" label="Is for adults (18+)?" hide-details />
              <v-checkbox v-model="form.isNewPatient" label="Is for new patients?" hide-details />
              <v-checkbox v-model="form.requireNote" label="Require patient to provide a note for appointment?" hide-details />
            </div>

            <div class="form-actions">
              <v-btn class="btn-primary" @click="saveAppointmentType()">Save</v-btn>
              <v-btn class="btn-secondary" @click="clearForm()">Cancel</v-btn>
              <v-btn class="btn-warning" :disabled="!form.id" @click="showDeleteDialog = true">Delete</v-btn>
            </div>
          </div>
        </v-col>
        <v-dialog v-model="showOpenDentalTypes" max-width="600px" persistent>
          <v-card class="modern-modal modal-fixed-actions">
            <v-card-title class="modal-title">Import from Open Dental</v-card-title>
            <v-card-text class="modal-content">
              <div v-if="openDentalAppointmentTypes.length === 0" class="no-od-types">
                <v-icon class="no-od-types-icon">mdi-calendar-remove</v-icon>
                <p class="no-od-types-text">No appointment types available</p>
              </div>
              <div v-else class="od-types-list">
                <div
                  v-for="(appointmentType, i) in openDentalAppointmentTypes"
                  :key="i"
                  @click="useOpenDentalAppointmentType(appointmentType)"
                  class="od-type-item">
                  <v-icon class="od-type-icon">mdi-calendar-import</v-icon>
                  <span class="od-type-name">{{ appointmentType.AppointmentTypeName }}</span>
                </div>
              </div>
            </v-card-text>
            <v-card-actions class="modal-actions">
              <v-btn class="btn-secondary" @click="showOpenDentalTypes = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="showDeleteDialog" max-width="500px" persistent>
          <v-card class="modern-modal">
            <v-card-title class="modal-title">Delete Appointment Type</v-card-title>
            <v-card-text class="modal-content">
              <p class="modal-question">Are you sure you want to delete {{ form.name }}?</p>
              <p class="modal-description">This action cannot be undone.</p>
            </v-card-text>
            <v-card-actions class="modal-actions">
              <v-btn class="btn-secondary" @click="showDeleteDialog = false">Cancel</v-btn>
              <v-btn class="btn-warning" @click="deleteAppointmentType()">Delete</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import DataService from '../../../_services/DataService';

export default {
  name: 'AppointmentTypes',
  props: [],
  components: {},
  data() {
    return {
      form: {
        id: '',
        name: '',
        pattern: '',
        appointmentLength: 0,
        codes: '',
        isMinor: false,
        isAdult: false,
        isNewPatient: false,
        requireNote: false,
        rank: 0,
        openDentalAppointmentTypeNumber: '',
        operatories: [],
      },
      acrewAppointmentTypes: [],
      openDentalAppointmentTypes: [],
      loading: true,
      showOpenDentalTypes: false,
      showDeleteDialog: false,
      allOperatories: [],
      selectedApptIndex: null,
      previousSelectedApptIndex: null,
      originalForm: {},
    };
  },
  methods: {
    async getOpenDentalOperatories() {
      const operatoriesResponse = await DataService.getOpenDentalOperatories();
      this.allOperatories = operatoriesResponse.data;
    },
    async getAppointmentTypes() {
      const appointmentTypes = await DataService.getAppointmentTypes();
      this.acrewAppointmentTypes = appointmentTypes.data.acrewAppointmentTypes;
      this.openDentalAppointmentTypes = appointmentTypes.data.openDentalAppointmentTypes;
      this.timeIncrementPreference = appointmentTypes.data.timeIncrementPreference;
    },
    selectAppointmentType(appt) {
      this.form = {
        id: appt.id,
        name: appt.name,
        pattern: this.createPatternBasedOnViewPreference(appt.pattern),
        appointmentLength: appt.appointmentLength,
        openDentalAppointmentTypeNumber: appt.openDentalAppointmentTypeNumber,
        codes: appt.codes,
        isMinor: appt.isMinor,
        isAdult: appt.isAdult,
        isNewPatient: appt.isNewPatient,
        requireNote: appt.requireNote,
        rank: appt.rank,
        operatories: appt.operatories.map((op) => op.openDentalOperatoryNumber),
      };
      this.originalForm = Object.assign({}, this.form);
    },
    useOpenDentalAppointmentType(appt) {
      this.previousSelectedApptIndex = this.selectedApptIndex;
      this.selectedApptIndex = null;
      this.form = {
        id: '',
        name: appt.AppointmentTypeName,
        pattern: this.createPatternBasedOnViewPreference(appt.Pattern),
        appointmentLength: `${appt.Pattern.length * 5}`,
        codes: appt.CodeStr,
        openDentalAppointmentTypeNumber: appt.AppointmentTypeNum,
        rank: `${this.acrewAppointmentTypes.length + 1}`,
        isMinor: false,
        isAdult: false,
        isNewPatient: false,
        requireNote: false,
        operatories: [],
      };
      this.originalForm = Object.assign({}, this.form);
      this.showOpenDentalTypes = false;
    },
    clearForm() {
      if (this.originalForm.id) {
        this.form = Object.assign({}, this.originalForm);
      } else if (this.previousSelectedApptIndex !== null && this.acrewAppointmentTypes.length > 0) {
        this.selectAppointmentType(this.acrewAppointmentTypes[this.previousSelectedApptIndex]);
        this.selectedApptIndex = this.previousSelectedApptIndex;
      } else if (this.acrewAppointmentTypes.length > 0) {
        this.selectAppointmentType(this.acrewAppointmentTypes[0]);
        this.selectedApptIndex = 0;
      }
    },
    createPatternBasedOnViewPreference(pattern) {
      let builtPattern = '';
      for (let i = 0; i < pattern.length; i += this.timeIncrementPreference / 5) {
        builtPattern += pattern[i];
      }

      return builtPattern;
    },
    turnViewPatternToSystemPattern(pattern) {
      let builtPattern = '';
      const repeatLength = this.timeIncrementPreference / 5;
      for (let i = 0; i < pattern.length; i++) {
        builtPattern += pattern[i].repeat(repeatLength);
      }

      return builtPattern;
    },
    async deleteAppointmentType() {
      this.showDeleteDialog = false;
      const deleted = await DataService.deleteAppointmentType(this.form.id);

      if (deleted.status === 204) {
        this.$root.$emit('raise_alert', {
          color: 'success',
          text: 'Appointment type deleted successfully',
        });
      } else {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: deleted.data || 'Something went wrong',
        });
      }

      this.loading = true;
      await this.getAppointmentTypes();

      if (this.acrewAppointmentTypes.length > 0) {
        this.selectAppointmentType(this.acrewAppointmentTypes[0]);
        this.selectedApptIndex = 0;
      }

      this.loading = false;
    },
    async saveAppointmentType() {
      const formDataWithNormalizedPattern = {
        ...this.form,
        pattern: this.turnViewPatternToSystemPattern(this.form.pattern),
      };

      let savedId = this.form.id;
      const isUpdate = !!this.form.id;

      if (isUpdate) {
        const updated = await DataService.updateAppointmentType(formDataWithNormalizedPattern);
        if (updated.status === 200) {
          this.$root.$emit('raise_alert', {
            color: 'success',
            text: 'Appointment type updated successfully!',
          });
        } else {
          this.$root.$emit('raise_alert', {
            color: 'error',
            text: updated.data || 'Something went wrong',
          });
        }
      } else {
        const created = await DataService.createAppointmentType(formDataWithNormalizedPattern);
        if (created.status === 201) {
          savedId = created.data.id;
          this.$root.$emit('raise_alert', {
            color: 'success',
            text: 'Appointment type created successfully!',
          });
        } else {
          this.$root.$emit('raise_alert', {
            color: 'error',
            text: created.data || 'Something went wrong',
          });
        }
      }

      this.loading = true;
      await this.getAppointmentTypes();

      const targetIndex = this.acrewAppointmentTypes.findIndex(a => a.id === savedId);
      if (targetIndex !== -1) {
        this.selectAppointmentType(this.acrewAppointmentTypes[targetIndex]);
        this.selectedApptIndex = targetIndex;
      }

      this.loading = false;
    },
  },
  async mounted() {
    this.loading = true;

    await Promise.all([
      this.getOpenDentalOperatories(),
      this.getAppointmentTypes(),
    ]);

    if (this.acrewAppointmentTypes.length > 0) {
      this.selectAppointmentType(this.acrewAppointmentTypes[0]);
      this.selectedApptIndex = 0;
    }

    this.loading = false;
  },
};

</script>

<style lang="scss" scoped>
@import '@/scss/variables.scss';

$appt-card-border-color: #e2e8f0;
$section-title-color: #1f2937;
$no-od-types-bg-color: #f8fafc;
$no-od-types-text-color: #9ca3af;
$modal-description-color: #6b7280;

.loading-container {
  text-align: center;
  padding: 80px 24px;
}

.appt-sidebar {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  height: fit-content;
  
  .sidebar-header {
    padding: 24px 24px 16px 24px;
    border-bottom: 1px solid #e2e8f0;
    
    .sidebar-title {
      font-size: 18px;
      font-weight: 600;
      color: #2d3748;
      margin: 0;
      font-family: 'Inter', sans-serif;
    }
  }
  
  .sidebar-content {
    padding: 16px;
  }
}

.appt-list {
  .no-results {
    text-align: center;
    padding: 40px 16px;
    
    .no-results-icon {
      font-size: 48px;
      color: #cbd5e0;
      margin-bottom: 12px;
    }
    
    .no-results-text {
      color: #718096;
      margin: 0;
      font-size: 14px;
    }
  }
  
  .appts-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .appt-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background-color: #edf2f7;
      border-color: #cbd5e0;
    }
    
    &.selected {
      background-color: #f0f9e8 !important;
      border-color: #B0D35E !important;
      
      .appt-name {
        color: #414a5b !important;
        font-weight: 600 !important;
      }
    }
    
    .appt-icon {
      color: #133b54 !important;
      margin-right: 12px !important;
      font-size: 20px !important;
    }
    
    .appt-name {
      font-weight: 500 !important;
      color: #1f2937 !important;
      font-size: 14px !important;
    }
  }
}

.no-selection {
  text-align: center;
  padding: 80px 24px;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid #e2e8f0;
  
  .no-selection-icon {
    font-size: 64px;
    color: #cbd5e0;
    margin-bottom: 16px;
  }
  
  .no-selection-text {
    color: #718096;
    margin: 0;
    font-size: 16px;
  }
}

.section-header-with-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 16px;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: $section-title-color;
    margin: 0;
    border: none;
  }
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 16px 0;
}

.v-card {
  border: 1px solid $appt-card-border-color !important;
  box-shadow: $shadow-sm !important;
}

.loading-spinner {
  display: block;
  margin: 40px auto;
}

.od-types-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.od-type-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #edf2f7;
    border-color: #cbd5e0;
  }
  
  .od-type-icon {
    color: #133b54 !important;
    margin-right: 12px !important;
    font-size: 20px !important;
  }
  
  .od-type-name {
    font-weight: 500 !important;
    color: #1f2937 !important;
    font-size: 14px !important;
  }
}

.no-od-types {
  text-align: center;
  padding: 40px 16px;
  background: $no-od-types-bg-color;
  border-radius: 8px;
  
  .no-od-types-icon {
    font-size: 48px;
    color: #cbd5e0;
    margin-bottom: 12px;
  }
  
  .no-od-types-text {
    color: $no-od-types-text-color;
    margin: 0;
    font-size: 14px;
  }
}

.modal-description {
  color: $modal-description-color;
  font-size: 14px;
  margin-top: 12px;
}
</style>
